import { useEffect, useRef, useState } from "react";
import { useRTC, formatRtcDuration } from "@/context/RTCContext";
import { useAuth } from "@/context/AuthContext";
import {
  MicrophoneSlash, Microphone, PhoneDisconnect, Users, ArrowsOut,
  PauseCircle, PlayCircle, DotsSixVertical,
} from "@phosphor-icons/react";

/**
 * Floating, draggable call dock — the default shell for an active call so
 * users can keep browsing Messages / CRM / Owner Console while the call
 * runs. Click "Expand" to open the full HelloByFlow-styled call window.
 *
 * Design tokens ONLY — matches Messages/Dashboard visual language.
 */
export default function CallDock() {
  const {
    activeRoom, peers, muted, onHold, duration, roomState, ringing,
    toggleMuted, toggleHold, leaveRoom, expandCall,
  } = useRTC();
  const { user } = useAuth();

  const [pos, setPos] = useState(() => {
    if (typeof window === "undefined") return { x: 24, y: 24 };
    return { x: window.innerWidth - 340, y: window.innerHeight - 200 };
  });
  const dragRef = useRef(null);
  const dragStateRef = useRef({ dragging: false, offX: 0, offY: 0 });

  const onDown = (e) => {
    const r = dragRef.current?.getBoundingClientRect();
    if (!r) return;
    dragStateRef.current = { dragging: true, offX: e.clientX - r.left, offY: e.clientY - r.top };
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      const st = dragStateRef.current;
      if (!st.dragging) return;
      const x = Math.max(8, Math.min(window.innerWidth  - 320, e.clientX - st.offX));
      const y = Math.max(8, Math.min(window.innerHeight - 160, e.clientY - st.offY));
      setPos({ x, y });
    };
    const onUp = () => { dragStateRef.current.dragging = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  if (!activeRoom) return null;

  const peerList  = Object.values(peers);
  const primary   = peerList[0]?.user;
  const totalCount = peerList.length + 1;

  const stateLabel = ringing ? "Ringing…"
    : roomState === "joining" ? "Connecting…"
    : roomState === "reconnecting" ? "Reconnecting…"
    : roomState === "connected" ? formatRtcDuration(duration)
    : "In call";

  const stateDot = ringing ? "#F59E0B"
    : roomState === "reconnecting" ? "#EF4444"
    : "#10B981";

  return (
    <div
      ref={dragRef}
      className="fixed z-[55] shadow-[0_20px_60px_-15px_rgba(11,21,38,0.35)]"
      style={{ left: pos.x, top: pos.y, width: 320 }}
      data-testid="call-dock"
    >
      <div
        className="rounded-2xl bg-white border border-[var(--hx-line)] overflow-hidden"
      >
        {/* Drag handle + expand */}
        <div
          className="flex items-center justify-between px-3 py-2 border-b border-[var(--hx-line)] cursor-grab active:cursor-grabbing bg-[var(--hx-line-soft)]"
          onMouseDown={onDown}
          data-testid="call-dock-drag-handle"
        >
          <div className="flex items-center gap-2 text-[var(--hx-ink-3)]">
            <DotsSixVertical size={14} />
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: stateDot }} />
              <span className="text-[11px] font-bold tracking-wide text-[var(--hx-ink-2)]">{stateLabel}</span>
            </div>
          </div>
          <button
            onClick={expandCall}
            className="p-1 rounded-md hover:bg-[var(--hx-blue-soft)] text-[var(--hx-ink-2)] hover:text-[var(--hx-blue)] transition-colors"
            title="Expand call"
            data-testid="call-dock-expand-btn"
          >
            <ArrowsOut size={14} weight="bold" />
          </button>
        </div>

        {/* Participants preview */}
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex -space-x-2">
            <AvatarDot user={user} />
            {peerList.slice(0, 2).map((p) => (
              <AvatarDot key={p.user.id} user={p.user} />
            ))}
            {peerList.length > 2 && (
              <div className="w-9 h-9 rounded-full bg-[var(--hx-line)] border-2 border-white flex items-center justify-center text-[11px] font-bold text-[var(--hx-ink-2)]">
                +{peerList.length - 2}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold text-[var(--hx-ink)] truncate" data-testid="call-dock-title">
              {primary?.name || activeRoom.title || "Internal call"}
            </div>
            <div className="text-[11px] text-[var(--hx-ink-3)] flex items-center gap-1">
              <Users size={11} />
              <span data-testid="call-dock-participant-count">{totalCount} in call</span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="px-3 pb-3 flex items-center gap-2">
          <DockBtn
            active={muted}
            onClick={() => toggleMuted()}
            label={muted ? "Unmute" : "Mute"}
            danger={muted}
            testid="call-dock-mute-btn"
          >
            {muted ? <MicrophoneSlash size={16} weight="fill" /> : <Microphone size={16} weight="fill" />}
          </DockBtn>
          <DockBtn
            active={onHold}
            onClick={toggleHold}
            label={onHold ? "Resume" : "Hold"}
            testid="call-dock-hold-btn"
          >
            {onHold ? <PlayCircle size={16} weight="fill" /> : <PauseCircle size={16} weight="fill" />}
          </DockBtn>
          <button
            onClick={leaveRoom}
            className="ml-auto h-9 px-3 rounded-lg bg-[#EF4444] hover:bg-[#DC2626] text-white text-[12px] font-bold flex items-center gap-1.5 transition-colors"
            data-testid="call-dock-end-btn"
          >
            <PhoneDisconnect size={14} weight="fill" /> End
          </button>
        </div>
      </div>
    </div>
  );
}

function AvatarDot({ user }) {
  const initials = (user?.name || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return user?.avatar_url ? (
    <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full border-2 border-white object-cover" />
  ) : (
    <div className="w-9 h-9 rounded-full border-2 border-white bg-[var(--hx-blue)] flex items-center justify-center text-white text-[11px] font-extrabold">
      {initials}
    </div>
  );
}

function DockBtn({ children, onClick, active, danger, label, testid }) {
  const cls = danger
    ? "bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA]"
    : active
      ? "bg-[var(--hx-blue-soft)] text-[var(--hx-blue)]"
      : "bg-[var(--hx-line-soft)] text-[var(--hx-ink-2)] hover:bg-[var(--hx-line)]";
  return (
    <button
      onClick={onClick}
      title={label}
      className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${cls}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
